'use client';

import { useLocale } from 'next-intl';
import { useEffectOnce, useSearchParam } from 'react-use';

import { HOST_LOCALE_DETECTION_ATTEMPTED_KEY } from '@/common/constants';
import { useConfigValue, useTenantKey } from '@/components/providers/config-provider';
import type { TargetDomainEntry } from '@/lib/types/tenant';
import { resolveHostLocale } from '@/lib/utils/host-locale';
import { changeLocale, hasManualLocaleChoice } from '@/lib/utils/locale';
import { buildAvailableLocaleSet } from '@/lib/utils/locale-shared';

function safeGetSessionItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetSessionItem({ key, value }: { key: string; value: string }) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage may be unavailable (e.g. partitioned storage blocked);
    // best-effort only — the caller still proceeds.
  }
}

export function useHostLanguageDetection({ enabled: callerEnabled = true } = {}) {
  const locale = useLocale();
  const tenantKey = useTenantKey();
  // parentUrl is user-controllable; safe to consume only because the resolved
  // locale is bounded to targetDomains[].locale (and further filtered against
  // meta.availableLocales below), so a crafter can at most pick one of the
  // tenant's own allowed locales.
  const parentUrl = useSearchParam('parentUrl');
  const targetDomains = useConfigValue<readonly TargetDomainEntry[]>(
    'navigation.iframeNavigation.targetDomains',
  );
  const availableLocales = useConfigValue<readonly string[]>('meta.availableLocales');

  useEffectOnce(() => {
    if (!callerEnabled) return;
    // Skip host-based switching after any explicit user locale choice.
    if (hasManualLocaleChoice()) return;
    if (!parentUrl) return;

    const target = resolveHostLocale({ parentUrl, targetDomains });
    if (!target || target === locale) return;

    const allowed = buildAvailableLocaleSet(availableLocales);
    if (!allowed.has(target)) return;

    // Cross-site iframe contexts may partition or block the `preferred-locale`
    // cookie set by `changeLocale()`, so on reload `useLocale()` would still
    // return the default locale and we'd reload again forever. Gate on a
    // per-target sessionStorage marker so each locale is attempted at most
    // once per browser session — even when cookies don't persist. Prefix with
    // tenant key to avoid collisions when multiple widgets share an origin.
    const retryKey = `${HOST_LOCALE_DETECTION_ATTEMPTED_KEY}:${tenantKey}`;
    if (safeGetSessionItem(retryKey) === target) return;
    safeSetSessionItem({ key: retryKey, value: target });

    changeLocale(target);
  });
}
